import {Button, Card, Checkbox, Dropdown, Flex, Input, Modal, Popover, Space} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUpWideShort, faPencil, faPlus, faRotate, faTrash} from "@fortawesome/free-solid-svg-icons";
import React, {useEffect} from "react";
import TextArea from "antd/es/input/TextArea";
import {useParams} from "react-router-dom";
import {categories} from "./list/SettingList";
import {
    generateSettingElementPrompt,
    improveSettingElementPrompt
} from "./prompts/ElementsPrompts";
import Title from "antd/es/typography/Title";
import {generateLoreItemPrompt, generateLoreItemsPrompt, improveLoreItemPrompt} from "./prompts/ItemsPrompts";
import {
    DraftComponent,
    DraftComponentCategory,
    DraftElement,
    DraftElementItem,
    embeddedItems
} from "../../../../../../feature/Draft";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {aiChainPrompt, getEmbedding, similaritySearch, unembed} from "../../../../../../ai/Prompt";
import {
    addLoreItem,
    deleteLoreItem,
    updateElement,
    updateLoreItem,
    updateLoreItems
} from "../../../../../../feature/project/draftSlice";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";
import {EmbedButton} from "../../../../../../ai/components/EmbedButton";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import {EmbeddingErrorMessage} from "../../../../../../ai/components/EmbeddingErrorMessage";

export const SettingElementDetails = (
    props: {
        category: DraftComponentCategory,
        component: DraftComponent,
        data: DraftElement,
    }
) => {
    const draft = useAppSelector(state => state.draftSlice.draft)
    const model = useAppSelector(state => state.draftSlice.model)
    const {projectId} = useParams()
    const [loading, setLoading] = React.useState(false)
    const [open, setOpen] = React.useState(false)
    const [useContext, setUseContext] = React.useState(true)
    const [document, setDocument] = React.useState<string[] | undefined>(undefined)
    const [showWarning, setShowWarning] = React.useState(false)
    const [unembedding, setUnembedding] = React.useState(false)
    const [prompt, setPrompt] = React.useState<string>(categories.find(c => c.key == props.category.category)?.prompt.element.extend + '\n\nЭлемент: ' + props.data.title)
    const dispatch = useAppDispatch()
    const [generating, setGenerating] = React.useState(false)
    const [generatingSingle, setGeneratingSingle] = React.useState(false)
    const [showEmbeddingWarning, setShowEmbeddingWarning] = React.useState(false)
    const [showElementsWarning, setShowElementsWarning] = React.useState(false)
    const [openItem, setOpenItem] = React.useState<string | undefined>(undefined)
    const [loadingItem, setLoadingItem] = React.useState<string | undefined>(undefined)
    const  aiProviders = useAppSelector(state => state.aiSlice.projectAiProviderSettings)

    const getEmbeddings = () => {
        if(aiProviders.map(p => p.name).includes('OpenAI')) {
            getEmbedding({
                project: projectId,
                ids: [props.category.category + '-component' + props.component.id + '-element' + props.data.id]
            })
                .then((data) => {
                    setDocument(data.documents)
                })
        }
    }

    useEffect(() => {
        getEmbeddings()
    }, [props.data.id]);

    useEffect(() => {
        const doc = mergeOverlappedStrings(document ?? [])?.replaceAll("\n", "").replaceAll(" ", "")
        const desc = props.data.description?.split("\n\n").join(" ")?.replaceAll("\n", "").replaceAll(" ", "")

        dispatch(updateElement({
            category: props.category.category,
            componentId: props.component.id,
            element: {
                ...props.data,
                embedded: (!doc || doc.trim().length == 0) ? false : doc == desc ? true : undefined
            }
        }))
    }, [document]);

    useEffect(() => {
        setPrompt(categories.find(c => c.key == props.category.category)?.prompt.element.extend + '\n\nЭлемент: ' + props.data.title)
    }, [props.data]);

    const generateElement = async () => {
        const category = categories.find(c => c.key == props.category.category)!
        let context = await similaritySearch({
            project: projectId,
            query: props.data.description ?? '',
            where: {'type': 'setting'}
        })
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: generateSettingElementPrompt({
                genres: draft.properties.genre,
                customPrompt: category.prompt.element.single + '\n\n"Элемент": ' + props.data.title,
                synopsis: draft.properties.synopsis,
                component: props.component,
                setting: draft.setting.description,
                context: (props.data.description ? [props.data.description] : []).concat(context.documents).flat()
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateElement({
            category: props.category.category,
            componentId: props.component.id,
            element: {
                ...props.data,
                description: data.data.description,
            }
        }))
    }

    const improveElement = async () => {
        const category = categories.find(c => c.key == props.category.category)!
        let context = useContext ? await similaritySearch({
            project: projectId,
            query: (props.data.description ?? '') + '\n' + prompt,
            where: {'type': 'setting'}
        }) : undefined
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: improveSettingElementPrompt({
                genres: draft.properties.genre,
                customPrompt: prompt ?? (category.prompt.element.extend + '\n\nЭлемент: ' + props.data.title),
                synopsis: draft.properties.synopsis,
                component: props.component,
                element: props.data,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateElement({
            category: props.category.category,
            componentId: props.component.id,
            element: {
                ...props.data,
                description: data.data.description,
            }
        }))
    }

    const generateItem = async (item?: DraftElementItem) => {
        const category = categories.find(c => c.key == props.category.category)!
        const query = item ? item.description : props.data.description
        let context = query ? await similaritySearch({
            project: projectId,
            query: query,
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}]}
        }) : undefined
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: generateLoreItemPrompt({
                category: category,
                genres: draft.properties.genre,
                customPrompt: category?.prompt.lore.single + (item ? ('\n\nЭлемент: ' + item?.title) : ('\n\nЭлемент: ' + props.data.title)),
                synopsis: draft.properties.synopsis,
                element: props.data,
                setting: draft.setting.description,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        if (item) {
            dispatch(updateLoreItem({
                // @ts-ignore
                category: props.category.category,
                componentId: props.component.id,
                elementId: props.data.id,
                item: {
                    ...item,
                    embedded: false,
                    description: data.data.description
                },
            }))
        } else {
            dispatch(addLoreItem({
                // @ts-ignore
                category: props.category.category,
                componentId: props.component.id,
                elementId: props.data.id,
                item: {
                    ...data.data,
                    embedded: false,
                    id: (props.data.items.flat().sort((a, b) => b.id - a.id)[0]?.id ?? 0) + 1,
                },
            }))
        }
    }

    const generateItems = async (force?: boolean) => {
        if (embeddedItems(props.data.items) == false) {
            if (props.data.items.length == 0 || force == true) {
                const category = categories.find(c => c.key == props.category.category)!

                let context = await similaritySearch({
                    project: projectId,
                    query: props.data.description ?? '',
                    where: {"$or": [{'type': 'lore'}, {'type': 'setting'}]}
                })

                let data = await aiChainPrompt({
                    projectId: projectId ?? "",
                    prompt: generateLoreItemsPrompt({
                        category: category,
                        genres: draft.properties.genre,
                        synopsis: draft.properties.synopsis,
                        setting: draft.setting.description,
                        element: props.data,
                        context: (props.data.description ? [props.data.description] : []).concat(context.documents).flat()
                    }),
                    model: model,
                    type: 'json'
                })

                dispatch(updateElement({
                    // @ts-ignore
                    category: props.category.category,
                    componentId: props.component.id,
                    element: {
                        ...props.data,
                        items: data.data.map((c: any) => ({...c, embedded: false})),
                    }
                }))
            } else {
                setShowElementsWarning(true)
            }
        } else {
            setShowEmbeddingWarning(true)
        }
    }

    const improveItem = async (item: DraftElementItem) => {
        const category = categories.find(c => c.key == props.category.category)!
        let context = useContext ? await similaritySearch({
            project: projectId,
            query: item.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}]}
        }) : undefined
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: improveLoreItemPrompt({
                genres: draft.properties.genre,
                customPrompt: prompt ?? (category.prompt.lore.extend + '\n\nЭлемент: ' + item.title),
                synopsis: draft.properties.synopsis,
                element: props.data,
                item: item,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateLoreItem({
            category: props.category.category,
            componentId: props.data.id,
            elementId: props.data.id,
            item: {
                ...item,
                description: data.data.description,
            }
        }))
    }

    const addEmptyItem = async () => {
        dispatch(addLoreItem({
            // @ts-ignore
            category: props.category.category,
            componentId: props.component.id,
            elementId: props.data.id,
            item: {
                title: '',
                description: '',
                embedded: false,
                id: (props.data.items?.flat().sort((a, b) => b.id - a.id)[0]?.id ?? 0) + 1,
            }
        }))
    }

    return (
        <Flex gap={10} vertical={true} style={{overflowY: 'auto', scrollbarWidth: "none"}}>
            <Card title={
                <Flex gap={10}>
                    <DebounceInput
                        style={{fontWeight: 'bold', background: "#fff"}}
                        placeholder={"Component title"}
                        value={props.data.title}
                        disabled={props.data.embedded}
                        onChange={(text) => {
                            dispatch(updateElement({
                                category: props.category.category ?? '',
                                componentId: props.data.id,
                                element: {
                                    ...props.data,
                                    title: text
                                }
                            }))
                        }}/>

                    <Popover
                        placement={'bottomRight'}
                        content={
                            <Space direction={'vertical'}>
                                <TextArea onChange={(e) => setPrompt(e.target.value)}
                                          value={prompt}
                                          autoSize={true}
                                          style={{width: 300}}
                                />
                                <Checkbox checked={useContext} onChange={(e) => {
                                    setUseContext(e.target.checked)
                                }}>Include context</Checkbox>
                                <Button
                                    type={'primary'}
                                    onClick={async () => {
                                        if (props.data.embedded) {
                                            setShowWarning(true)
                                        } else {
                                            setLoading(true)
                                            setOpen(false)
                                            await improveElement()
                                            setLoading(false)
                                        }
                                    }}>Go</Button>
                            </Space>
                        }
                        title="Prompt"
                        trigger="click"
                        open={open}
                        onOpenChange={(e) => setOpen(e)}
                    >
                        <GenButton
                            mode={'icon'}
                            hideMainAction={true}
                            loading={loading}
                            icon={<IconBoltGrad />}
                            open={!open}
                            actions={[{
                                key: "1",
                                title: "Re-generate",
                                action: async (model: string) => {
                                    if (props.data.embedded) {
                                        setShowWarning(true)
                                    } else {
                                        await generateElement()
                                            .catch((e) => {
                                                console.log(e)
                                            })
                                    }
                                },
                                render: (key, title, action) => {
                                    return <Space><BoltIcon secondary={faRotate} size={16}/>{title}</Space>
                                }
                            },
                                {
                                    key: "2", title: "Improve", action: async (model: string) => {
                                        if (props.data.embedded) {
                                            setShowWarning(true)
                                        } else {
                                            await improveElement()
                                        }
                                    }, render: (key, title, action) => {
                                        return <Flex gap={10} justify={'space-between'}>
                                            <Space><BoltIcon secondary={faArrowUpWideShort} size={16}/>{title}
                                            </Space><Button type={'text'} size={'small'}
                                                            icon={<FontAwesomeIcon icon={faPencil}/>}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setOpen(true)
                                                            }}/>
                                        </Flex>
                                    }
                                }]
                            }/>

                    </Popover>

                    {props.data.description && props.data.description?.length > 0 ?
                        <EmbedButton
                            data={{
                                id: props.category.category + '-component' + props.component.id + '-element' + props.data.id,
                                text: props.data.description ?? '',
                                metadata:
                                    {
                                        title: props.data.title ?? '',
                                        category: props.category.category ?? '',
                                        type: 'setting'
                                    },

                            }}
                            chunkSize={800}
                            chunkOverlap={100}
                            embedded={props.data.embedded}
                            edited={false}
                            onEmbedded={(data) => {
                                dispatch(updateElement({
                                    category: props.category.category,
                                    componentId: props.component.id,
                                    element: {
                                        ...props.data,
                                        embedded: true
                                    }
                                }))
                                getEmbeddings()
                            }}
                            onUnembedded={(data) => {
                                dispatch(updateElement({
                                    category: props.category.category,
                                    componentId: props.component.id,
                                    element: {
                                        ...props.data,
                                        embedded: false
                                    }
                                }))
                                getEmbeddings()
                            }}
                        />
                        : ''}
                </Flex>
            }
            >
                <TextAreaActions
                    style={{border: "none"}}
                    autoSize={true}
                    value={props.data.description}
                    onActionSelected={() => {
                        if (props.data.embedded) {
                            setShowWarning(true)
                            return false
                        } else {
                            return true
                        }
                    }}
                    validate={text => {
                        if (props.data.embedded) {
                            setShowWarning(true)
                            return false
                        } else {
                            return true
                        }
                    }}
                    onChange={(text: string) => {
                        dispatch(updateElement({
                            category: props.category.category,
                            componentId: props.component.id,
                            element: {
                                ...props.data,
                                description: text
                            }
                        }))
                    }}
                />
            </Card>

            <Card>
                <Flex justify={'space-between'} align={'center'} wrap={'wrap'}>
                    <Title level={5} style={{margin: 0}}>Lore items</Title>
                    <Space>
                        <GenButton
                            style={{width: 130}}
                            type={'primary'}
                            loading={generating}
                            actions={[{
                                key: "1", title: "Generate", action: async (model: string) => {
                                    await generateItems()
                                        .catch((e) => {
                                            console.log(e)
                                        })
                                }
                            }]}
                        />

                        <Dropdown
                            trigger={['click']}
                            menu={{
                                items: [
                                    {
                                        key: "empty",
                                        label: <Space><FontAwesomeIcon icon={faPlus} style={{width: 16}}/>New empty
                                            element</Space>,
                                    },
                                    {
                                        key: "gen",
                                        label: <Space><BoltIcon secondary={faPlus} size={16}/>Generate new
                                            element</Space>,
                                    }
                                ],
                                onClick: async (e) => {
                                    if (e.key == 'empty') {
                                        await addEmptyItem()
                                    } else if (e.key == 'gen') {
                                        setGeneratingSingle(true)
                                        await generateItem().finally(() => {
                                            setGeneratingSingle(false)
                                        })

                                    }
                                }
                            }}
                        >
                            <Button
                                loading={generatingSingle}
                                icon={<FontAwesomeIcon icon={faPlus}/>}
                            />
                        </Dropdown>
                    </Space>
                </Flex>
            </Card>
            <Space direction={'vertical'}>
                {props.data?.items?.map((item, index) =>
                    <Card style={{flexGrow: 1}} key={index}
                          title={<Flex justify={'space-between'}>
                              <Flex gap={10} style={{flexGrow: 1}}>
                                  <div style={{
                                      height: 30,
                                      minWidth: 30,
                                      width: 30,
                                      borderRadius: 30,
                                      background: "#ffffff",
                                      color: "#000",
                                      textAlign: "center",
                                      padding: 3
                                  }}>{index + 1}</div>
                                  <DebounceInput
                                      style={{fontWeight: 'bold', background: "#fff"}}
                                      placeholder={"Component title"}
                                      value={item.title}
                                      disabled={item.embedded}
                                      onChange={(text) => {
                                          dispatch(updateLoreItem({
                                              category: props.category.category ?? '',
                                              componentId: props.component.id,
                                              elementId: props.data.id,
                                              item: {
                                                  ...item,
                                                  title: text
                                              }
                                          }))
                                      }}/>

                                  <Space>
                                      <Popover
                                          placement={'bottomRight'}
                                          content={
                                              <Space direction={'vertical'}>
                                                  <TextArea onChange={(e) => setPrompt(e.target.value)}
                                                            value={prompt}
                                                            autoSize={true}
                                                            style={{width: 300}}
                                                  />
                                                  <Checkbox checked={useContext} onChange={(e) => {
                                                      setUseContext(e.target.checked)
                                                  }}>Include context</Checkbox>
                                                  <Button
                                                      type={'primary'}
                                                      onClick={async () => {
                                                          if (item.embedded) {
                                                              setShowEmbeddingWarning(true)
                                                          } else {
                                                              setLoadingItem("item-" + item.id)
                                                              setOpenItem(undefined)
                                                              await improveItem(item)
                                                              setLoadingItem(undefined)
                                                          }
                                                      }}>Go</Button>
                                              </Space>
                                          }
                                          title="Prompt"
                                          trigger="click"
                                          open={openItem == "item-" + item.id}
                                          onOpenChange={(e) => {
                                              setOpenItem(e ? "item-" + item.id : undefined)
                                          }}
                                      >
                                          <GenButton
                                              mode={'icon'}
                                              hideMainAction={true}
                                              loading={loadingItem == "item-" + item.id}
                                              open={openItem == undefined}
                                              icon={<IconBoltGrad/>}
                                              actions={[{
                                                  key: "1",
                                                  title: "Re-generate",
                                                  action: async (model: string) => {
                                                      if (item.embedded) {
                                                          setShowEmbeddingWarning(true)
                                                      } else {
                                                          await generateItem(item)
                                                              .catch((e) => {
                                                                  console.log(e)
                                                              })
                                                      }
                                                  },
                                                  render: (key, title, action) => {
                                                      return <Space>
                                                          <BoltIcon
                                                              secondary={faRotate}
                                                              size={16}/>{title}</Space>
                                                  }
                                              },
                                                  {
                                                      key: "2", title: "Improve", action: async (model: string) => {
                                                          if (item.embedded) {
                                                              setShowEmbeddingWarning(true)
                                                          } else {
                                                              await improveItem(item)
                                                          }
                                                      }, render: (key, title, action) => {
                                                          return <Flex gap={10} justify={'space-between'}>
                                                              <Space>
                                                                  <BoltIcon
                                                                      secondary={faArrowUpWideShort}
                                                                      size={16}/>{title}
                                                              </Space>
                                                              <Button
                                                                  type={'text'} size={'small'}
                                                                  icon={<FontAwesomeIcon icon={faPencil}/>}
                                                                  onClick={(e) => {
                                                                      setPrompt(categories.find(c => c.key == props.category.category)?.prompt.lore.extend + '\n\nЭлемент: ' + item.title)
                                                                      e.stopPropagation()
                                                                      setOpenItem("item-" + item.id)
                                                                  }}/>
                                                          </Flex>
                                                      }
                                                  }]
                                              }/>

                                      </Popover>
                                      {item.description && item.description?.length > 0 ?
                                          <EmbedButton
                                              data={{
                                                  id: props.category.category + '-component' + props.data.id + '-element' + item.id,
                                                  text: item.description ?? '',
                                                  metadata:
                                                      {
                                                          title: item.title ?? '',
                                                          category: props.category.category ?? '',
                                                          type: 'setting'
                                                      },

                                              }}
                                              chunkSize={800}
                                              chunkOverlap={100}
                                              embedded={item.embedded}
                                              edited={false}
                                              onEmbedded={(data) => {
                                                  dispatch(updateLoreItem({
                                                      category: props.category.category,
                                                      componentId: props.component.id,
                                                      elementId: props.data.id,
                                                      item: {
                                                          ...item,
                                                          embedded: true
                                                      }
                                                  }))
                                              }}
                                              onUnembedded={(data) => {
                                                  dispatch(updateLoreItem({
                                                      category: props.category.category,
                                                      componentId: props.component.id,
                                                      elementId: props.data.id,
                                                      item: {
                                                          ...item,
                                                          embedded: false
                                                      }
                                                  }))
                                              }}
                                          />
                                          : ''}
                                      <Button
                                          icon={<FontAwesomeIcon icon={faTrash}/>}
                                          disabled={item.embedded}
                                          style={{background: "#fff"}}
                                          onClick={() => {
                                              dispatch(deleteLoreItem({
                                                  // @ts-ignore
                                                  category: props.category.category ?? '',
                                                  componentId: props.component.id,
                                                  elementId: props.data.id,
                                                  itemId: item.id
                                              }))
                                          }}/>

                                  </Space>
                              </Flex>
                          </Flex>}>

                        <TextAreaActions
                            style={{border: "none"}}
                            autoSize={true}
                            disabled={item.embedded}
                            value={item.description}
                            validate={text => true}
                            onChange={(text: string) => {
                                dispatch(updateLoreItem({
                                    category: props.category.category ?? '',
                                    componentId: props.component.id,
                                    elementId: props.data.id,
                                    item: {
                                        ...item,
                                        description: text
                                    }
                                }))
                            }}
                        />
                    </Card>
                )}
            </Space>

            {showWarning ? <EmbeddingErrorMessage
                onClose={() => setShowWarning(false)}
                content={<>
                    <div>You are editing an embedded element:</div>
                    <ul>
                        <li style={{fontWeight: '700'}}>{props.data.title}</li>
                    </ul>
                    <div>You have to un-embed it before editing</div>
                </>}
                loading={unembedding}
                onUnmbedClick={() => {
                    setUnembedding(true)
                    unembed({
                        project: projectId,
                        ids: [props.category.category + '-component' + props.component.id + '-element' + props.data.id]
                    })
                        .then(() => {
                            dispatch(updateElement({
                                category: props.category.category,
                                componentId: props.component.id,
                                element: {
                                    ...props.data,
                                    embedded: false
                                }
                            }))
                        }).finally(() => {
                        setUnembedding(false)
                        setShowWarning(false)
                        getEmbeddings()
                    })
                }}
            /> : ''}
            {showEmbeddingWarning ? <EmbeddingErrorMessage
                onClose={() => setShowEmbeddingWarning(false)}
                content={<>
                    <div>You are trying to regenerate embedded elements:</div>
                    <ul>
                        {props.data.items.filter(e => e.embedded).map(e => <li
                            style={{fontWeight: '700'}}>{e.title}</li>)}
                    </ul>
                    <div>You have to un-embed it before editing</div>
                </>}
                loading={unembedding}
                onUnmbedClick={() => {
                    setUnembedding(true)
                    unembed({
                        project: projectId,
                        ids: props.data.items.filter(e => e.embedded).map(e => props.category.category + '-component' + props.data.id + '-element' + e.id)
                    })
                        .then(() => {
                            dispatch(updateLoreItems({
                                category: props.category.category,
                                componentId: props.data.id,
                                elementId: props.data.id,
                                items: props.data.items.filter(e => e.embedded).map(e => ({
                                    ...e,
                                    embedded: false
                                }))
                            }))
                        }).finally(() => {
                        setUnembedding(false)
                        setShowEmbeddingWarning(false)
                    })
                }}
            /> : ''}
            {showElementsWarning ? <Modal
                open={true}
                title={'Generation Warning'}
                cancelText={'Cancel'}
                okText={'Generate'}
                onCancel={() => setShowElementsWarning(false)}
                onOk={async () => {
                    setShowElementsWarning(false)
                    setGenerating(true)
                    await generateItems(true)
                    setGenerating(false)
                }}
            >
                <Space direction={'vertical'}>
                    <div style={{whiteSpace: 'pre-wrap'}}>You already have generated lore items for this element.
                        <ul>
                            {props.data.items.map(e => <li style={{fontWeight: 700}}>{e.title}</li>)}
                        </ul>
                        If you choose <strong>Generate</strong> option then existing data will be replaced with
                        newly generated data. You will not be able to undone this action.
                    </div>
                </Space>
            </Modal> : ''}
        </Flex>
    )
}


export function mergeOverlappedStrings(strings: string[]) {
    let merged = strings[0]
    for (let i = 1; i < strings.length; i++) {
        const string = strings[i]
        const overlap = findOverlap(merged, string)
        if (overlap > 0) {
            merged = merged.slice(0, -overlap) + string
        } else {
            merged += string
        }
    }
    return merged
}

function findOverlap(string1: string, string2: string) {
    let overlap = 0
    for (let i = 1; i <= string1.length; i++) {
        if (string2.startsWith(string1.slice(-i))) {
            overlap = i
        }
    }
    return overlap
}